import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import fallbackAudioSrc from '../assets/images/djnight.mp4';

// Stable context: track info + actions (changes only on track/queue changes)
const PlayerStateContext = createContext();
// Volatile context: progress + playback state (changes every second during playback)
const PlayerProgressContext = createContext();

function isJsdomMediaEnvironment() {
  return typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
}

function safePause(audio) {
  if (!audio || isJsdomMediaEnvironment()) {
    return;
  }

  try {
    audio.pause();
  } catch {
    // jsdom media methods are not implemented.
  }
}

function safeLoad(audio) {
  if (!audio || isJsdomMediaEnvironment()) {
    return;
  }

  try {
    audio.load();
  } catch {
    // jsdom media methods are not implemented.
  }
}

function safePlay(audio, onFailure) {
  if (!audio || isJsdomMediaEnvironment()) {
    return;
  }

  try {
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(onFailure);
    }
  } catch {
    if (onFailure) {
      onFailure();
    }
  }
}

function getDurationSeconds(track) {
  const parts = String(track?.duration || '')
    .split(':')
    .map((part) => Number(part));

  if (parts.some((part) => Number.isNaN(part))) {
    return 0;
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return Number.isFinite(parts[0]) ? parts[0] : 0;
}

function getSeekDuration(audio, track) {
  if (audio?.duration && Number.isFinite(audio.duration)) {
    return audio.duration;
  }

  return getDurationSeconds(track);
}

function seekAudioToProgress(audio, track, progressPercent) {
  const seekDuration = getSeekDuration(audio, track);

  if (!audio || !seekDuration) {
    return false;
  }

  audio.currentTime = (progressPercent / 100) * seekDuration;
  return true;
}

export function PlayerProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [queue, setQueue] = useState([]);
  const audioRef = useRef(null);
  const currentTrackRef = useRef(currentTrack);
  const queueRef = useRef(queue);
  const pendingSeekRef = useRef(null);

  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    if (typeof Audio === 'undefined') {
      return undefined;
    }

    const audio = new Audio();
    audio.preload = 'metadata';
    audio.volume = 1;
    audioRef.current = audio;

    const handleEnded = () => {
      const activeTrack = currentTrackRef.current;
      const activeQueue = queueRef.current;

      if (!activeTrack || activeQueue.length === 0) {
        setIsPlaying(false);
        setProgress(100);
        return;
      }

      const currentIndex = activeQueue.findIndex((track) => track.id === activeTrack.id);

      if (currentIndex === -1 || activeQueue.length === 1) {
        audio.currentTime = 0;
        setIsPlaying(false);
        setProgress(100);
        return;
      }

      const nextIndex = currentIndex < activeQueue.length - 1 ? currentIndex + 1 : 0;
      setCurrentTrack(activeQueue[nextIndex]);
      setIsPlaying(true);
      setProgress(0);
    };

    audio.addEventListener('ended', handleEnded);

    return () => {
      safePause(audio);
      audio.removeEventListener('ended', handleEnded);
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  const updateProgress = useCallback((val) => {
    const audio = audioRef.current;

    if (typeof val === 'function') {
      setProgress(prev => {
        const next = Math.max(0, Math.min(100, val(prev)));
        if (!seekAudioToProgress(audio, currentTrackRef.current, next)) {
          pendingSeekRef.current = next;
        }
        return next;
      });
      return;
    }

    const next = Math.max(0, Math.min(100, val));
    if (!seekAudioToProgress(audio, currentTrackRef.current, next)) {
      pendingSeekRef.current = next;
    }
    setProgress(next);
  }, []);

  const updateVolume = useCallback((val) => {
    setVolume((current) => {
      const next = Math.max(0, Math.min(1, typeof val === 'function' ? val(current) : val));
      const audio = audioRef.current;

      if (audio) {
        audio.volume = next;
      }

      return next;
    });
  }, []);

  const play = useCallback((track, trackList) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    setProgress(0);
    if (trackList) setQueue(trackList);
    else setQueue([track]);
  }, []);

  const pause = useCallback(() => setIsPlaying(false), []);
  const resume = useCallback(() => setIsPlaying(true), []);
  const togglePlay = useCallback(() => setIsPlaying(prev => !prev), []);

  const skipNext = useCallback(() => {
    const activeTrack = currentTrackRef.current;
    const activeQueue = queueRef.current;

    if (!activeTrack || activeQueue.length === 0) {
      return;
    }

    const idx = activeQueue.findIndex(t => t.id === activeTrack.id);
    if (idx === -1) {
      return;
    }

    const nextIdx = idx < activeQueue.length - 1 ? idx + 1 : 0;
    setCurrentTrack(activeQueue[nextIdx]);
    setIsPlaying(true);
    setProgress(0);
  }, []);

  const skipPrev = useCallback(() => {
    const activeTrack = currentTrackRef.current;
    const activeQueue = queueRef.current;

    if (!activeTrack || activeQueue.length === 0) {
      return;
    }

    const idx = activeQueue.findIndex(t => t.id === activeTrack.id);
    if (idx === -1) {
      return;
    }

    const prevIdx = idx > 0 ? idx - 1 : activeQueue.length - 1;
    setCurrentTrack(activeQueue[prevIdx]);
    setIsPlaying(true);
    setProgress(0);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !currentTrack) {
      return;
    }

    const nextSrc = currentTrack.audioSrc || fallbackAudioSrc;

    if (audio.src !== nextSrc) {
      audio.src = nextSrc;
      safeLoad(audio);
      setProgress(0);
    }

    audio.loop = !currentTrack.audioSrc;

    if (pendingSeekRef.current !== null) {
      const pendingSeek = pendingSeekRef.current;
      if (seekAudioToProgress(audio, currentTrack, pendingSeek)) {
        pendingSeekRef.current = null;
        setProgress(pendingSeek);
      }
    }
  }, [currentTrack]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !currentTrack) {
      return;
    }

    if (!isPlaying) {
      safePause(audio);
      return;
    }

    safePlay(audio, () => {
      setIsPlaying(false);
    });
  }, [currentTrack, isPlaying]);

  useEffect(() => {
    if (!isPlaying || !currentTrack) {
      return undefined;
    }

    const durationSeconds = getDurationSeconds(currentTrack);

    if (!durationSeconds) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setProgress((prev) => Math.min(100, prev + (0.25 / durationSeconds) * 100));
    }, 250);

    return () => window.clearInterval(interval);
  }, [currentTrack, isPlaying]);

  const stableValue = useMemo(() => ({
    currentTrack, queue, play, pause, resume, togglePlay, skipNext, skipPrev,
  }), [currentTrack, queue, play, pause, resume, togglePlay, skipNext, skipPrev]);

  const progressValue = useMemo(() => ({
    isPlaying, progress, updateProgress, volume, updateVolume,
  }), [isPlaying, progress, updateProgress, volume, updateVolume]);

  return (
    <PlayerStateContext.Provider value={stableValue}>
      <PlayerProgressContext.Provider value={progressValue}>
        {children}
      </PlayerProgressContext.Provider>
    </PlayerStateContext.Provider>
  );
}

/** Stable player state: track, queue, actions. Does NOT rerender on progress ticks. */
export function usePlayer() {
  const ctx = useContext(PlayerStateContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
}

/** Volatile playback state: isPlaying, progress, updateProgress. Rerenders every progress tick. */
export function usePlayerProgress() {
  const ctx = useContext(PlayerProgressContext);
  if (!ctx) throw new Error('usePlayerProgress must be used within PlayerProvider');
  return ctx;
}
